import type { ChangeEvent } from 'react';
import { forwardRef } from 'react';

import { Controller, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import type { InputProps } from '@polyai/ui/components/atoms/Input';

import Password from 'components/molecules/PasswordInput';
import type { SharedFormProps } from './types';

type Props<T> = SharedFormProps<T> &
  Omit<InputProps, 'type'> & {
    allowAutofill?: boolean;
  };

export const FormPasswordInput = forwardRef<HTMLInputElement, Props<any>>(
  function FormPasswordInput<T>(
    { controlKey, onChange, allowAutofill = false, ...rest }: Props<T>,
    ref: React.Ref<HTMLInputElement>,
  ) {
    const { control } = useFormContext();
    return (
      <Controller
        control={control}
        name={controlKey as string}
        render={({ field, fieldState: { error } }) => {
          const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            field.onChange(value);
            if (onChange) {
              onChange(event);
            }
          };
          // https://developer.mozilla.org/en-US/docs/Web/Security/Practical_implementation_guides/Turning_off_form_autocompletion#managing_autofill_for_login_fields
          const autoCompleteProps: React.InputHTMLAttributes<HTMLInputElement> =
            allowAutofill === false ? { autoComplete: 'new-password' } : {};
          const passwordProps = {
            ...field,
            errors: !!error ? [error.message || ''] : [],
            onChange: handleChange,
            ...autoCompleteProps,
            ...rest,
            ref,
          };
          return (
            <Password
              className={twMerge('!font-normal', rest.className)}
              {...passwordProps}
            />
          );
        }}
      />
    );
  },
);
